import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Send, Clock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { BRAND, WHATSAPP_LINK } from "../brand";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        phone: form.phone || undefined,
        subject: form.subject || undefined,
      });
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#f7f5f2]">
      <Seo
        title="Contact ooloo — Book Kitchen Help in Lucknow"
        description="Reach ooloo on WhatsApp +91 73180 86600 or info@oolookitchen.in. Mon–Sun 7 AM – 9 PM. Serving Gomtinagar, Gomtinagar Extension and Sushant Golf City in Lucknow."
        path="/contact"
      />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-12">
        <p className="eyebrow rise">Contact</p>
        <h1 className="font-display mt-5 text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl rise rise-1">
          We&apos;re here to help —
          <span className="italic font-serif font-light text-[#c45b3e]"> seven days a week</span>.
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="grid lg:grid-cols-12 gap-5">
          {/* FORM */}
          <div className="lg:col-span-7 bento-card p-8 md:p-12">
            <h2 className="font-display text-3xl font-extrabold tracking-tight">
              Send us a message
            </h2>
            <p className="mt-2 text-[#5c5a56]">
              Average reply time under 30 minutes during business hours.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-name">Name</Label>
                  <Input
                    id="c-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    data-testid="contact-name-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input
                    id="c-phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="contact-phone-input"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-email">Email</Label>
                  <Input
                    id="c-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    data-testid="contact-email-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="c-subject">Subject</Label>
                  <Input
                    id="c-subject"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    data-testid="contact-subject-input"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="c-message">Message</Label>
                <Textarea
                  id="c-message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  required
                  data-testid="contact-message-input"
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="bg-[#0a0a0a] text-white hover:bg-[#333333] rounded-full px-8 py-6 text-base font-bold"
              >
                <Send size={16} className="mr-2" />
                {submitting ? "Sending…" : "Send message"}
              </Button>
            </form>
          </div>

          {/* INFO COL */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            <div className="dark-card p-8 md:p-10">
              <p className="eyebrow text-white/60">Reach us</p>
              <ul className="mt-6 space-y-5">
                <li className="flex items-start gap-3">
                  <MapPin size={20} className="text-[#c45b3e] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-display font-bold text-lg">Location</p>
                    <p className="text-white/70 text-sm">
                      {BRAND.city} — {BRAND.zones.join(", ")}. By appointment.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={20} className="text-[#c45b3e] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-display font-bold text-lg">Call</p>
                    <a
                      href={`tel:+${BRAND.whatsappCountry}${BRAND.whatsappNumberRaw}`}
                      data-testid="contact-call-link"
                      className="text-white/85 link-underline"
                    >
                      {BRAND.phoneDisplay}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={20} className="text-[#c45b3e] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-display font-bold text-lg">Email</p>
                    <a
                      href={`mailto:${BRAND.email}`}
                      data-testid="contact-email-link"
                      className="text-white/85 link-underline"
                    >
                      {BRAND.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock size={20} className="text-[#c45b3e] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-display font-bold text-lg">Hours</p>
                    <p className="text-white/70 text-sm">
                      Mon–Sun · 7:00 am to 9:00 pm
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bento-card overflow-hidden p-0">
              <iframe
                title="ooloo Lucknow"
                src="https://www.google.com/maps?q=Lucknow&output=embed"
                className="w-full h-[260px] border-0"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>

      {/* BIG WA CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="bg-[#25d366] rounded-[2rem] p-10 md:p-16 grain text-[#0a2a14] relative overflow-hidden">
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <p className="eyebrow text-[#0a2a14]/70">Fastest way to reach us</p>
              <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
                Just say hi on WhatsApp.
              </h2>
              <p className="mt-4 max-w-lg text-[#0a2a14]/80 text-lg">
                One thread for bookings, deliveries, subscriptions and special requests.
                Real humans, real fast.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-big-whatsapp-cta"
                className="inline-flex items-center gap-3 bg-[#0a0a0a] text-white hover:bg-[#1a1a1a] rounded-full px-8 py-5 font-bold text-lg transition"
              >
                <MessageCircle size={20} strokeWidth={2.5} />
                {BRAND.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
